/**
 * taskStore — état global des tâches de l'utilisateur UNIQUE.
 *
 * Persistance :
 *   - Web/dev : localStorage via middleware zustand/persist (clé "unique-tasks")
 *   - Electron : remplacé par un bridge IPC vers le repository Drizzle (étape 10)
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { NewTaskInput, Quadrant, Task, TaskStatus } from './types';

interface TaskState {
  tasks: Task[];

  addTask: (input: NewTaskInput) => Task;
  updateTask: (id: string, patch: Partial<Task>) => void;
  removeTask: (id: string) => void;
  moveTask: (id: string, quadrant: Quadrant) => void;
  setStatus: (id: string, status: TaskStatus) => void;
  completeTask: (id: string) => void;
  addFocusMinutes: (id: string, minutes: number) => void;
  hydrate: (tasks: Task[]) => void;
  reset: () => void;

  getByQuadrant: (quadrant: Quadrant) => Task[];
  countByQuadrant: () => Record<Quadrant, number>;
  getActive: () => Task[];
}

const nowIso = (): string => new Date().toISOString();

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `task_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
};

export const useTaskStore = create<TaskState>()(
  persist(
    (set, get) => ({
      tasks: [],

      addTask: (input) => {
        const task: Task = {
          id: generateId(),
          status: input.status ?? 'todo',
          aiScore: input.aiScore ?? 50,
          tags: input.tags ?? [],
          actualMinutes: 0,
          createdAt: nowIso(),
          updatedAt: nowIso(),
          ...input,
        };
        set((state) => ({ tasks: [task, ...state.tasks] }));
        return task;
      },

      updateTask: (id, patch) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, ...patch, updatedAt: nowIso() } : t,
          ),
        })),

      removeTask: (id) =>
        set((state) => ({ tasks: state.tasks.filter((t) => t.id !== id) })),

      moveTask: (id, quadrant) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, quadrant, updatedAt: nowIso() } : t,
          ),
        })),

      setStatus: (id, status) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, status, updatedAt: nowIso() } : t,
          ),
        })),

      completeTask: (id) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, status: 'done', updatedAt: nowIso() } : t,
          ),
        })),

      addFocusMinutes: (id, minutes) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id
              ? {
                  ...t,
                  actualMinutes: t.actualMinutes + Math.max(0, minutes),
                  updatedAt: nowIso(),
                }
              : t,
          ),
        })),

      hydrate: (tasks) => set({ tasks }),
      reset: () => set({ tasks: [] }),

      getByQuadrant: (quadrant) =>
        get()
          .tasks.filter((t) => t.quadrant === quadrant && t.status !== 'done')
          .sort((a, b) => b.aiScore - a.aiScore),

      countByQuadrant: () => {
        const counts: Record<Quadrant, number> = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
        for (const t of get().tasks) {
          if (t.status !== 'done') counts[t.quadrant]++;
        }
        return counts;
      },

      getActive: () => get().tasks.filter((t) => t.status !== 'done'),
    }),
    {
      name: 'unique-tasks',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({ tasks: state.tasks }),
    },
  ),
);

/* -------------------------------------------------------------------------- */
/* Données de démonstration                                                   */
/* -------------------------------------------------------------------------- */

export function buildDemoTasks(): Task[] {
  const now = new Date();
  const plusHours = (h: number): string =>
    new Date(now.getTime() + h * 3600 * 1000).toISOString();
  const base = {
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    actualMinutes: 0,
    tags: [] as string[],
  };
  return [
    {
      ...base,
      id: 'demo-1',
      title: 'Valider le budget Q2 avec la DAF',
      description: 'Signature avant la réunion du comité de direction.',
      quadrant: 'Q1',
      source: 'email',
      status: 'todo',
      aiScore: 92,
      dueAt: plusHours(3),
      estimatedMinutes: 45,
    },
    {
      ...base,
      id: 'demo-2',
      title: 'Rappeler le partenaire logistique à Bobo',
      quadrant: 'Q1',
      source: 'call',
      status: 'todo',
      aiScore: 85,
      dueAt: plusHours(5),
      estimatedMinutes: 20,
    },
    {
      ...base,
      id: 'demo-3',
      title: 'Rédiger la note stratégique 2026',
      description: 'Vision à 18 mois — draft pour le CA.',
      quadrant: 'Q2',
      source: 'manual',
      status: 'in_progress',
      aiScore: 74,
      dueAt: plusHours(48),
      estimatedMinutes: 120,
    },
    {
      ...base,
      id: 'demo-4',
      title: 'Préparer le plan de formation RH',
      quadrant: 'Q2',
      source: 'ai',
      status: 'todo',
      aiScore: 68,
      dueAt: plusHours(72),
      estimatedMinutes: 90,
    },
    {
      ...base,
      id: 'demo-5',
      title: 'Répondre aux factures fournisseurs',
      quadrant: 'Q3',
      source: 'email',
      status: 'todo',
      aiScore: 55,
      dueAt: plusHours(6),
      estimatedMinutes: 30,
    },
    {
      ...base,
      id: 'demo-6',
      title: 'Classer les comptes-rendus de mars',
      quadrant: 'Q4',
      source: 'inherited',
      status: 'todo',
      aiScore: 22,
      dueAt: null,
      estimatedMinutes: 40,
      delegateTo: 'assistant@entreprise.bf',
    },
  ];
}
